import type Phaser from "phaser";

export interface PhaserDisplayCountOptions {
  readonly depthBucketSize?: number;
  readonly maxDepthBuckets?: number;
  /**
   * 활성인데 숨겨진 객체를 type/name 버킷으로 추가 집계한다.
   * 예: hiddenActiveText_segmentedTabLabel 8
   */
  readonly maxHiddenActiveBuckets?: number;
  /** renderable 객체를 type/name 버킷으로 추가 집계한다. */
  readonly maxRenderableBuckets?: number;
}

const countKeyOf = (type: string | undefined): string | null => {
  switch (type) {
    case "Text":
      return "text";
    case "Image":
    case "Sprite":
      return "image";
    case "Graphics":
      return "graphics";
    case "Container":
      return "container";
    case "Rectangle":
      return "rectangle";
    case "Zone":
      return "zone";
    case "NineSlice":
      return "nineSlice";
    case "TileSprite":
      return "tileSprite";
    default:
      return null;
  }
};

const canRender = (type: string | undefined): boolean => type !== "Zone";

function bump(counts: Record<string, number>, key: string, suffix = ""): void {
  counts[`${key}${suffix}`] = (counts[`${key}${suffix}`] ?? 0) + 1;
}

const sanitizeCountKey = (value: string): string =>
  value
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 36) || "unnamed";

export function countPhaserDisplayList(
  list: readonly Phaser.GameObjects.GameObject[],
  options: PhaserDisplayCountOptions = {},
): Record<string, number> {
  const depthBucketSize = Math.max(1, Math.floor(options.depthBucketSize ?? 10));
  const maxDepthBuckets = Math.max(0, Math.floor(options.maxDepthBuckets ?? 6));
  const maxHiddenActiveBuckets = Math.max(0, Math.floor(options.maxHiddenActiveBuckets ?? 0));
  const maxRenderableBuckets = Math.max(0, Math.floor(options.maxRenderableBuckets ?? 0));
  const counts: Record<string, number> = {
    display: list.length,
    displayActive: 0,
    displayVisible: 0,
    displayRenderable: 0,
    text: 0,
    textRenderable: 0,
    image: 0,
    imageRenderable: 0,
    graphics: 0,
    graphicsRenderable: 0,
    container: 0,
    containerRenderable: 0,
    displayHiddenActive: 0,
    displayAlphaHidden: 0,
  };
  const buckets = new Map<number, number>();
  const hiddenActiveBuckets = new Map<string, number>();
  const renderableBuckets = new Map<string, number>();

  for (const obj of list) {
    const o = obj as Phaser.GameObjects.GameObject & {
      visible?: boolean;
      alpha?: number;
      type?: string;
      depth?: number;
    };
    const active = o.active !== false;
    const visible = o.visible === true;
    const alpha = o.alpha ?? 1;
    if (active) counts.displayActive += 1;
    if (visible) counts.displayVisible += 1;
    if (active && !visible) {
      counts.displayHiddenActive += 1;
      if (maxHiddenActiveBuckets > 0) {
        const typeName = sanitizeCountKey(o.type ?? "unknown");
        const objectName = sanitizeCountKey(o.name || "unnamed");
        const bucketKey = `hiddenActive${typeName}_${objectName}`;
        hiddenActiveBuckets.set(bucketKey, (hiddenActiveBuckets.get(bucketKey) ?? 0) + 1);
      }
    }
    if (active && visible && alpha <= 0) counts.displayAlphaHidden += 1;
    const renderable = active && visible && alpha > 0 && canRender(o.type);
    if (renderable) {
      counts.displayRenderable += 1;
      const depth = typeof o.depth === "number" && Number.isFinite(o.depth) ? o.depth : 0;
      const bucket = Math.floor(depth / depthBucketSize) * depthBucketSize;
      buckets.set(bucket, (buckets.get(bucket) ?? 0) + 1);
      if (maxRenderableBuckets > 0) {
        const typeName = sanitizeCountKey(o.type ?? "unknown");
        const objectName = sanitizeCountKey(o.name || "unnamed");
        const bucketKey = `renderable${typeName}_${objectName}`;
        renderableBuckets.set(bucketKey, (renderableBuckets.get(bucketKey) ?? 0) + 1);
      }
    }
    const key = countKeyOf(o.type);
    if (key) {
      bump(counts, key);
      if (active) bump(counts, key, "Active");
      if (active && !visible) bump(counts, key, "HiddenActive");
      if (active && visible && alpha <= 0) bump(counts, key, "AlphaHidden");
      if (renderable) bump(counts, key, "Renderable");
    }
  }

  const topBuckets = [...buckets.entries()]
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .slice(0, maxDepthBuckets);
  for (let i = 0; i < topBuckets.length; i += 1) {
    const [depth, count] = topBuckets[i]!;
    counts[`depth${depth}`] = count;
  }
  const topHiddenActiveBuckets = [...hiddenActiveBuckets.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, maxHiddenActiveBuckets);
  for (const [key, count] of topHiddenActiveBuckets) counts[key] = count;
  const topRenderableBuckets = [...renderableBuckets.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, maxRenderableBuckets);
  for (const [key, count] of topRenderableBuckets) counts[key] = count;
  return counts;
}
